import { useCallback, useEffect, useRef, useState } from 'react';
import type { JobDto } from '@gc/shared';
import { ApiError, apiRequest } from '../../api/client.js';

export type PanelTrabajosProps = { readonly projectId: string };

const ETIQUETA_TIPO: Record<string, string> = {
  GENERATE_TEXT: 'Estructura de escenas',
  GENERATE_IMAGE: 'Imagen',
  GENERATE_VOICE: 'Narración',
  GENERATE_MUSIC: 'Música',
  GENERATE_VIDEO: 'Clip de vídeo',
  POLL_GENERATION: 'Consulta al proveedor',
  RENDER_PROJECT: 'Render',
};

const ETIQUETA_ESTADO: Record<string, string> = {
  PENDING: 'En cola',
  RUNNING: 'Ejecutando',
  COMPLETED: 'Completado',
  FAILED: 'Error',
  CANCELLED: 'Cancelado',
};

const ESTADOS_ACTIVOS = new Set(['PENDING', 'RUNNING']);

/** Mensaje accionable por categoría de error, en lugar del texto técnico. */
const AYUDA_ERROR: Record<string, string> = {
  AUTH: 'La credencial del proveedor no es válida. Revísala en Ajustes de IA.',
  QUOTA: 'El proveedor no tiene crédito o cuota disponible.',
  RATE_LIMIT: 'El proveedor limitó las peticiones. Se reintenta automáticamente.',
  SAFETY_REJECTED: 'El proveedor rechazó el contenido por sus políticas. Cambia el texto y vuelve a intentarlo.',
  TIMEOUT_UNKNOWN:
    'No se supo el resultado y la llamada pudo cobrarse. Revísalo antes de reintentar para no pagarla dos veces.',
  INVALID_REQUEST: 'La petición no era válida. Revisa los datos de la escena.',
  TRANSIENT: 'Fallo pasajero del proveedor.',
  INTERNAL: 'Error interno al procesar el trabajo.',
};

/**
 * Progreso por polling (docs/02_ARCHITECTURE.md). Se consulta solo mientras hay
 * trabajos activos: sin ellos, el intervalo se detiene en lugar de golpear la
 * API indefinidamente.
 */
export function PanelTrabajos({ projectId }: PanelTrabajosProps): React.JSX.Element | null {
  const [jobs, setJobs] = useState<JobDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [accionando, setAccionando] = useState<string | null>(null);
  const activos = useRef(false);

  const recargar = useCallback(async () => {
    try {
      const respuesta = await apiRequest<{ data: JobDto[] }>(`/projects/${projectId}/jobs?limit=20`, {
        raw: true,
      });
      setJobs(respuesta.data);
      activos.current = respuesta.data.some((job) => ESTADOS_ACTIVOS.has(job.status));
      setError(null);
    } catch (causa) {
      setError(causa instanceof ApiError ? causa.message : 'No se pudo consultar el progreso.');
    }
  }, [projectId]);

  useEffect(() => {
    void recargar();
    const intervalo = setInterval(() => {
      // Sin trabajos activos no hay nada que refrescar.
      if (activos.current) void recargar();
    }, 2_000);
    return () => clearInterval(intervalo);
  }, [recargar]);

  async function accion(jobId: string, ruta: 'cancel' | 'retry'): Promise<void> {
    setAccionando(jobId);
    try {
      await apiRequest(`/jobs/${jobId}/${ruta}`, { method: 'POST' });
      await recargar();
    } catch (causa) {
      setError(causa instanceof ApiError ? causa.message : 'La operación falló.');
    } finally {
      setAccionando(null);
    }
  }

  if (jobs.length === 0 && error === null) return null;

  return (
    <div>
      <h2>Trabajos</h2>
      {error !== null && (
        <p role="alert" style={{ color: 'var(--error)' }}>
          {error}
        </p>
      )}
      <ul style={{ listStyle: 'none', padding: 0, display: 'grid', gap: '0.5rem' }}>
        {jobs.map((job) => {
          const activo = ESTADOS_ACTIVOS.has(job.status);
          const cancelacionPedida = job.cancellationRequestedAt !== null;
          return (
            <li
              key={job.id}
              style={{ border: '1px solid var(--borde)', borderRadius: '6px', padding: '0.5rem 0.75rem' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <strong style={{ flex: 1 }}>{ETIQUETA_TIPO[job.type] ?? job.type}</strong>
                <span>{ETIQUETA_ESTADO[job.status] ?? job.status}</span>
                {activo && <progress value={job.progress} max={100} />}
                {job.attemptCount > 1 && (
                  <small>
                    intento {job.attemptCount}/{job.maxAttempts}
                  </small>
                )}
                {/* Cancelar solo mientras está activo; reintentar solo si falló. */}
                {activo && !cancelacionPedida && (
                  <button
                    type="button"
                    onClick={() => void accion(job.id, 'cancel')}
                    disabled={accionando !== null}
                  >
                    Cancelar
                  </button>
                )}
                {job.status === 'FAILED' && (
                  <button
                    type="button"
                    onClick={() => void accion(job.id, 'retry')}
                    disabled={accionando !== null}
                  >
                    Reintentar
                  </button>
                )}
              </div>

              {cancelacionPedida && activo && (
                <p style={{ margin: '0.25rem 0 0' }}>
                  <small>
                    Cancelación solicitada. Si el proveedor ya empezó, puede terminar y facturarse;
                    el resultado se guardaría en la biblioteca sin aplicarse al montaje.
                  </small>
                </p>
              )}

              {job.error !== null && (
                <p style={{ margin: '0.25rem 0 0', color: 'var(--error)' }}>
                  <small>
                    {AYUDA_ERROR[job.error.kind] ?? job.error.message}
                    {job.error.possiblyBilled === true && ' (pudo facturarse)'}
                  </small>
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
